/**
 * Review Sessions Page
 * 
 * Displays all review sessions from .cursor/reports/review-session-*
 * Shows ALL file contents at once for easy scrolling.
 * Click a file in the sidebar to auto-scroll to it.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useReviewSessions } from "./useReviewSessions";
import { getFileContent, getFileType } from "@/lib/erp/reviewSessions";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FileText, Code, FolderOpen, RefreshCw, ChevronRight, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface FileData {
  path: string;
  content: string;
  loading: boolean;
  error: string | null;
}

export function ReviewSessionsPage() {
  const navigate = useNavigate();
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [filesData, setFilesData] = useState<Map<string, FileData>>(new Map());
  const { data: sessions, isLoading, refetch, isRefetching } = useReviewSessions();
  
  const currentSession = sessions?.find(s => s.id === selectedSession);
  const fileRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const contentAreaRef = useRef<HTMLDivElement>(null);

  // Auto-select first session if none selected
  useEffect(() => {
    if (sessions?.length && !selectedSession) {
      setSelectedSession(sessions[0].id);
    }
  }, [sessions, selectedSession]);

  // Load all files when session changes
  useEffect(() => {
    if (!currentSession) return;

    const loadAllFiles = async () => {
      const newFilesData = new Map<string, FileData>();
      
      // Initialize all files as loading
      for (const filePath of currentSession.files) {
        newFilesData.set(filePath, { path: filePath, content: '', loading: true, error: null });
      }
      setFilesData(newFilesData);

      // Load each file
      for (const filePath of currentSession.files) {
        try {
          const content = await getFileContent(currentSession.id, filePath);
          setFilesData(prev => {
            const updated = new Map(prev);
            updated.set(filePath, { path: filePath, content, loading: false, error: null });
            return updated;
          });
        } catch (error) {
          setFilesData(prev => {
            const updated = new Map(prev);
            updated.set(filePath, { 
              path: filePath, 
              content: '', 
              loading: false, 
              error: error instanceof Error ? error.message : 'Failed to load file'
            });
            return updated;
          });
        }
      }
    };

    loadAllFiles();
  }, [currentSession]);

  // Scroll to file when clicked
  const scrollToFile = useCallback((filePath: string) => {
    const element = fileRefs.current.get(filePath);
    if (element && contentAreaRef.current) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Register file ref
  const setFileRef = useCallback((filePath: string, element: HTMLDivElement | null) => {
    if (element) {
      fileRefs.current.set(filePath, element);
    } else {
      fileRefs.current.delete(filePath);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)] text-muted-foreground">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
        Loading review sessions...
      </div>
    );
  }

  if (!sessions?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] text-center p-8">
        <FolderOpen className="h-16 w-16 text-muted-foreground/50 mb-4" />
        <h1 className="text-2xl font-bold mb-2">No Review Sessions</h1>
        <p className="text-muted-foreground mb-4 max-w-md">
          No review sessions found. Run <code className="bg-muted px-2 py-1 rounded-md">/copy-to-review</code> in Cursor to create one.
        </p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-background">
      {/* Sessions Sidebar */}
      <div className="w-64 border-r bg-muted/30 flex flex-col shrink-0">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="font-semibold text-sm">Sessions</h2>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => refetch()}
            disabled={isRefetching}
            title="Refresh sessions"
            className="h-7 w-7"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", isRefetching && "animate-spin")} />
          </Button>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {sessions.map((session) => (
              <button
                key={session.id}
                onClick={() => setSelectedSession(session.id)}
                className={cn(
                  "w-full text-left p-2.5 rounded-sm hover:bg-muted transition-colors group",
                  selectedSession === session.id && "bg-muted ring-1 ring-primary/20"
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-xs truncate">{session.id}</span>
                  <ChevronRight className={cn(
                    "h-3.5 w-3.5 text-muted-foreground transition-transform",
                    selectedSession === session.id && "rotate-90"
                  )} />
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {session.fileCount} files
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Files Sidebar */}
      {currentSession && (
        <div className="w-56 border-r bg-muted/10 flex flex-col shrink-0">
          <div className="p-3 border-b">
            <h3 className="font-semibold text-xs">Files</h3>
            <p className="text-[10px] text-muted-foreground mt-0.5">{currentSession.fileCount} files • Click to scroll</p>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-1.5 space-y-0.5">
              {currentSession.files.map((file) => {
                const fileName = file.split('/').pop() || file;
                const isNested = file.includes('/');
                const fileType = getFileType(file);
                const fileData = filesData.get(file);
                
                return (
                  <button
                    key={file}
                    onClick={() => scrollToFile(file)}
                    className="w-full text-left px-2 py-1.5 rounded-xs hover:bg-muted transition-colors flex items-center gap-1.5"
                    title={file}
                  >
                    <FileIcon type={fileType} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-medium truncate">{fileName}</div>
                      {isNested && (
                        <div className="text-[9px] text-muted-foreground truncate">
                          {file.split('/').slice(0, -1).join('/')}
                        </div>
                      )}
                    </div>
                    {fileData?.loading && (
                      <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
                    )}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* All Files Content Viewer */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <div className="p-3 border-b bg-muted/20 shrink-0">
          <div className="font-medium text-sm">
            {currentSession ? `All Files (${currentSession.fileCount})` : 'Select a session'}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Scroll to review all files • Click file in sidebar to jump
          </div>
        </div>
        
        <div ref={contentAreaRef} className="flex-1 overflow-auto">
          {currentSession ? (
            <div className="p-4 space-y-6">
              {currentSession.files.map((filePath) => {
                const fileData = filesData.get(filePath);
                const fileName = filePath.split('/').pop() || filePath;
                const fileType = getFileType(filePath);
                
                return (
                  <div
                    key={filePath}
                    ref={(el) => setFileRef(filePath, el)}
                    className="scroll-mt-4"
                  >
                    {/* File Header */}
                    <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b pb-2 mb-3">
                      <div className="flex items-center gap-2">
                        <FileIcon type={fileType} />
                        <span className="font-medium text-sm">{fileName}</span>
                      </div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5 pl-6">
                        {filePath}
                      </div>
                    </div>
                    
                    {/* File Content */}
                    {fileData?.loading ? (
                      <div className="flex items-center text-muted-foreground p-4 bg-muted/20 rounded-sm">
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        Loading file...
                      </div>
                    ) : fileData?.error ? (
                      <div className="text-red-500 p-4 bg-red-50 rounded-sm text-sm">
                        Error: {fileData.error}
                      </div>
                    ) : (
                      <FileContentViewer 
                        content={fileData?.content || ''} 
                        fileType={fileType} 
                      />
                    )}
                  </div>
                );
              })}
              
              {/* End spacer */}
              <div className="h-32" />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a session to view files</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FileIcon({ type }: { type: string }) {
  const iconClass = "h-4 w-4 shrink-0";
  
  switch (type) {
    case 'markdown':
      return <FileText className={cn(iconClass, "text-blue-500")} />;
    case 'typescript':
      return <Code className={cn(iconClass, "text-blue-600")} />;
    case 'javascript':
      return <Code className={cn(iconClass, "text-yellow-500")} />;
    case 'python':
      return <Code className={cn(iconClass, "text-green-500")} />;
    case 'json':
      return <Code className={cn(iconClass, "text-orange-500")} />;
    default:
      return <FileText className={cn(iconClass, "text-muted-foreground")} />;
  }
}

function FileContentViewer({ content, fileType: _fileType }: { content: string; fileType: string }) {
  const lines = content.split('\n');
  const lineNumberWidth = String(lines.length).length;
  
  return (
    <div className="font-mono text-sm border rounded-sm overflow-hidden">
      <pre className="bg-muted/30 overflow-x-auto">
        <code>
          {lines.map((line, index) => (
            <div key={index} className="flex hover:bg-muted/50 transition-colors">
              <span 
                className="select-none text-muted-foreground px-3 py-0.5 text-right border-r border-muted bg-muted/20 shrink-0" 
                style={{ minWidth: `${Math.max(lineNumberWidth + 2, 4)}ch` }}
              >
                {index + 1}
              </span>
              <span className="px-4 py-0.5 whitespace-pre">{line || ' '}</span>
            </div>
          ))}
        </code>
      </pre>
    </div>
  );
}

export default ReviewSessionsPage;
